// Mirayama day to night: one quiet "Day · Night" switch crossfades every frame in its scope
// between the day and the night photograph. The words under the images follow the switch.
// Used by the homepage section (mm-day-night) and the product page toggle (mm-day-night-toggle).
if (!customElements.get('mm-day-night')) {
  customElements.define(
    'mm-day-night',
    class MmDayNight extends HTMLElement {
      connectedCallback() {
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.buttons = Array.from(this.querySelectorAll('[data-dn-set]'));
        this.layers = Array.from(this.querySelectorAll('[data-dn-layer]'));
        this.words = Array.from(this.querySelectorAll('[data-dn-word]'));

        this.buttons.forEach((button) => {
          button.addEventListener('click', () => {
            this.stopAuto();
            this.set(button.dataset.dnSet);
          });
          // Start fetching the other photographs as soon as someone reaches for the switch.
          button.addEventListener('pointerenter', () => this.warm(), { once: true });
          button.addEventListener('focus', () => this.warm(), { once: true });
        });

        this.set(this.dataset.state || '', true);
        this.setupNav();
        this.setupAuto();
      }

      disconnectedCallback() {
        this.stopAuto();
      }

      warm() {
        this.layers.forEach((img) => {
          if (img.loading === 'lazy') img.loading = 'eager';
        });
      }

      set(state, initial = false) {
        if (!initial) this.warm();
        this.dataset.state = state;
        this.buttons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.dnSet === state)));
        this.layers.forEach((img) => img.setAttribute('aria-hidden', String(img.dataset.dnLayer !== state)));
        if (!state) return;
        const word = state === 'night' ? this.dataset.nightWord : this.dataset.dayWord;
        if (word) this.words.forEach((w) => (w.textContent = word));
      }

      // Optional: change between day and night by itself while on screen.
      setupAuto() {
        const seconds = parseInt(this.dataset.auto, 10) || 0;
        if (!seconds || this.reduced) return;
        this.paused = false;
        this.visible = false;
        this.addEventListener('mouseenter', () => (this.paused = true));
        this.addEventListener('mouseleave', () => (this.paused = false));
        this.addEventListener('focusin', () => (this.paused = true));
        this.addEventListener('focusout', () => (this.paused = false));
        this.observer = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          if (this.visible) this.warm();
        });
        this.observer.observe(this);
        this.timer = setInterval(() => {
          if (this.paused || !this.visible || document.hidden) return;
          this.set(this.dataset.state === 'night' ? 'day' : 'night');
        }, seconds * 1000);
      }

      stopAuto() {
        clearInterval(this.timer);
        this.timer = null;
        if (this.observer) this.observer.disconnect();
      }

      // Quiet arrows for the row of tiles (shown only when the row overflows).
      setupNav() {
        this.track = this.querySelector('[data-dn-track]');
        this.nav = this.querySelector('[data-dn-nav]');
        if (!this.track || !this.nav) return;
        this.prev = this.nav.querySelector('[data-dn-prev]');
        this.next = this.nav.querySelector('[data-dn-next]');
        this.prev.addEventListener('click', () => this.page(-1));
        this.next.addEventListener('click', () => this.page(1));
        this.track.addEventListener('scroll', () => this.updateNav(), { passive: true });
        new ResizeObserver(() => this.updateNav()).observe(this.track);
        this.updateNav();
      }

      page(direction) {
        const tile = this.track.querySelector('.mm-dn__tile');
        if (!tile) return;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const step = tile.getBoundingClientRect().width + gap;
        const perView = Math.max(1, Math.round((this.track.clientWidth + gap) / step));
        this.track.scrollBy({ left: direction * step * perView, behavior: this.reduced ? 'auto' : 'smooth' });
      }

      updateNav() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        this.nav.hidden = max <= 1;
        this.prev.disabled = this.track.scrollLeft <= 1;
        this.next.disabled = this.track.scrollLeft >= max - 1;
      }
    }
  );
}
